import { useCallback, useState } from "react";

import toast from "react-hot-toast";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button, Icon, Loader, Message } from "simplydo/react/ui";

import api from "api";

import type {
  CompanyCreditsafe,
  CreditRatingValue,
} from "components/innointel/types";
import RedactedInfo from "components/lib/RedactedInfo";

import {
  InfoBoxWidget,
  InfoBoxWidgetTitleArea,
  KeyFact,
  KeyFactLinkSeeMore,
} from "../Styles";
import CreditsafeManualMatch from "./CreditsafeManualMatch";

const CreditRatingColourMap: Record<CreditRatingValue, string> = {
  A: "MediumSeaGreen",
  B: "MediumSeaGreen",
  C: "MediumSeaGreen",
  D: "Orange",
  E: "Orange",
};

const hiddenContentLineWidths = [72, 81, 65, 78, 69, 84];

const getCreditRatingColour = (creditRating: string) =>
  creditRating in CreditRatingColourMap ?
    CreditRatingColourMap[creditRating as CreditRatingValue]
  : "red";

const GetCreditContainer = styled.div`
  margin-top: 15px;
  position: relative;
  .fake-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    .circle {
      background-color: rgb(200, 200, 200);
      height: 75px;
      width: 75px;
      border-radius: 50%;
      margin-right: 10px;
    }
    .lines {
      display: flex;
      flex-direction: column;
      flex: 1;
    }
  }
  .button-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const CreditContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CreditRatingDisplay = styled.div<{ $creditRating: string }>`
  border-radius: 50%;
  height: 65px;
  width: 65px;
  background-color: ${({ $creditRating }) =>
    getCreditRatingColour($creditRating)};
  display: flex;
  justify-content: center;
  align-items: center;
  > span {
    display: block;
    color: #fff;
    font-weight: bold;
    font-size: 2.5em;
  }
`;

type CreditsafeReportSummaryProps = { creditsafe: CompanyCreditsafe };

export const CreditsafeReportSummary = ({
  creditsafe,
}: CreditsafeReportSummaryProps) => {
  const reportData = creditsafe.report?.data;
  const currentCreditRating = reportData?.creditScore?.currentCreditRating;
  const latestRatingChangeDate =
    reportData?.creditScore?.latestRatingChangeDate;
  const commonValue = currentCreditRating?.commonValue ?? "";

  if (!currentCreditRating?.commonValue) {
    return null;
  }

  return (
    <CreditContainer>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
        }}
      >
        <CreditRatingDisplay $creditRating={commonValue}>
          <span>{commonValue}</span>
        </CreditRatingDisplay>
        <KeyFact
          title="Credit rating"
          style={{ marginBottom: 0 }}
          fact={
            <>
              Rating: {currentCreditRating.commonValue},{" "}
              {currentCreditRating.commonDescription}.<br />
              {latestRatingChangeDate ?
                <span style={{ color: "gray", fontSize: "0.9rem" }}>
                  Last updated{" "}
                  {formatDate(latestRatingChangeDate, "MMM d, yyyy p")}
                </span>
              : null}
            </>
          }
        />
      </div>
    </CreditContainer>
  );
};

type CompanyCreditsafeProps = {
  companyId: string;
  name: string;
  location?: OpenAPI.Schemas["InnointelCompanies"]["location"];
  creditsafe: CompanyCreditsafe;
  company: OpenAPI.Schemas["InnointelCompanies"];
};

const CompanyCreditsafe = ({
  companyId,
  name,
  location,
  creditsafe,
  company,
}: CompanyCreditsafeProps) => {
  const [reportLoading, setReportLoading] = useState(false);
  const [manualMatchOpen, setManualMatchOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [creditsafeErrorMessage, setCreditsafeErrorMessage] = useState("");

  const getReportData = useCallback(() => {
    setReportLoading(true);
    api.innointel.getCreditsafeReport(
      companyId,
      "",
      ({ report }) => {
        setReportLoading(false);
        if (report) {
          api.invalidateQueries(api.innointel.getCompany, [companyId]);
        }
      },
      (err) => {
        toast.error(err.message);
        setCreditsafeErrorMessage(err.message);
        setReportLoading(false);
      },
    );
  }, [companyId]);

  const getReportPdf = useCallback(() => {
    setPdfLoading(true);
    api.innointel.getCreditsafeReportPdf(
      companyId,
      ({ report, didFindReport: didFind }) => {
        setPdfLoading(false);
        if (didFind) {
          window.open(report.path, "_blank");
          api.invalidateQueries(api.innointel.getCompany, [companyId]);
        } else {
          toast.error("Could not fetch detailed report.");
        }
      },
      (err) => {
        toast.error(err.message);
        setPdfLoading(false);
      },
    );
  }, [companyId]);

  return (
    <InfoBoxWidget $relaxed={!!creditsafeErrorMessage}>
      <CreditsafeManualMatch
        isOpen={manualMatchOpen}
        onClose={() => setManualMatchOpen(false)}
        companyId={companyId}
        name={name}
        location={location}
        onError={(err) => {
          setCreditsafeErrorMessage(err);
          setManualMatchOpen(false);
        }}
      />

      {creditsafeErrorMessage ?
        <Message negative>
          <Message.Header>Could not fetch credit information</Message.Header>
          <p>
            CreditSafe is currently unable to process this request. We are
            unable to fetch credit information at this time. Please try again
            later or contact us directly if this issue persists.
          </p>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "row",
            }}
          >
            <Button
              compact
              size="small"
              basic
              content="Try again"
              onClick={() => {
                setCreditsafeErrorMessage("");
              }}
            />
          </div>
        </Message>
      : <>
          <InfoBoxWidgetTitleArea $noMargin>
            <Icon name="money" />
            <h3>Credit information</h3>
          </InfoBoxWidgetTitleArea>
          {creditsafe.name ?
            <div>
              <KeyFact title="Company name" fact={creditsafe.name} />
            </div>
          : null}
          {creditsafe.status ?
            <div>
              <KeyFact title="Company status" fact={creditsafe.status} />
            </div>
          : null}
          {creditsafe.vatNo ?
            <div>
              <KeyFact title="VAT number" fact={creditsafe.vatNo} />
            </div>
          : null}
          {creditsafe.safeNo ?
            <div>
              <KeyFact title="SAFE number" fact={creditsafe.safeNo} />
            </div>
          : null}
          {!creditsafe.report ?
            creditsafe.id && company.status === "processing" ?
              <Loader
                content={<h5>Looking up credit information</h5>}
                active
                inline
              />
            : <GetCreditContainer>
                <div className="fake-info">
                  <div className="circle" />
                  <div className="lines">
                    {hiddenContentLineWidths.map((width, i) => (
                      <RedactedInfo key={i} width={width} />
                    ))}
                  </div>
                </div>
                <div className="button-container">
                  {!creditsafe.name ?
                    <Button
                      loading={reportLoading}
                      content="Attach credit details"
                      onClick={() => setManualMatchOpen(true)}
                      primary
                    />
                  : <Button
                      loading={reportLoading}
                      content="Get detailed report"
                      onClick={() => getReportData()}
                      primary
                    />
                  }
                </div>
              </GetCreditContainer>

          : <>
              <CreditsafeReportSummary creditsafe={creditsafe} />
              <KeyFactLinkSeeMore>
                {pdfLoading && (
                  <Loader
                    inverted
                    active
                    inline
                    size="tiny"
                    style={{ marginRight: 5 }}
                  />
                )}
                <Button secondary size="tiny" onClick={getReportPdf}>
                  <Icon name="file pdf" /> Generate detailed PDF report
                </Button>
              </KeyFactLinkSeeMore>
            </>
          }
        </>
      }
    </InfoBoxWidget>
  );
};

export default CompanyCreditsafe;
